import { useEffect, useState } from "react";
import { generatePath } from "react-router-dom";
import { LoadingSpinner, UniversalPageLayout, Button } from "../../components/BaseComponents";
import Pagination from "../../components/BaseComponents/Pagination/Pagination";
import SimpleSortTable, { TableColumn } from "../../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import CreatePortfolioModal from "./CreatePortfolioModal";
import { formatDate } from "../../lib/utils";
import DeletePortfolioModal from "./DeletePortfolioModal";
import useCleanup from "../../hooks/useCleanup";
import EditPortfolioModal from "./EditPortfolioModal";
import usePagination from "../../hooks/usePagination";
import useApi from "../../hooks/useApi";
import routes from "../../components/Router/routes";
import { usePortfolioListZones } from "./usePortfolioListZones";


function PortfolioList() {
    const columns: TableColumn[] = [
        {
            id: 'name',
            label: 'Portfolio name',
        },
        {
            id: 'created',
            label: 'Date created',
            renderer: (key, row) => <span>{ formatDate(row[key]) }</span>
        },
        {
            id: 'companyCount',
            label: 'Organisation count',
        },
        {
            id: 'options',
            label: 'Actions',
            textAlign: 'center',
            renderer: (columnKey, row) => {
                return (
                    <div className="space-x-2">
                        <Button requiresWrite type="Primary" icon="fa-solid fa-trash" hasLeadingIcon size="sm" isTableRowButton onClick={() => setDeletionCandidate(row)} />
                        <Button requiresWrite type="Primary" icon="fa-solid fa-pen-to-square" hasLeadingIcon size="sm" isTableRowButton onClick={() => setEditCandidate(row)} />
                    </div>
                )
            }
        },
        {
            id: 'view',
            label: 'View / refine data',
            renderer: (columnKey, row) => {
                return (
                    <Button
                        type="Primary"
                        text="View portfolio"
                        size="sm"
                        isLink
                        url={generatePath(routes.companyPortfolio.path, { portfolioId: row['id']})}
                    />
                )
            }
    
        }
    ]

    const pagination = usePagination();

    const [portfolios, setPortfolios] = useState<any[]>([]);
    const [portfolioSummary, setPortfolioSummary] = useState<any>(null);
    const [summaryError, setSummaryError] = useState<any>();
    const api = useApi();

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const openCreateModal = () => setCreateModalOpen(true);

    const [deletionCandidate, setDeletionCandidate] = useState<any>(null);
    const [editCandidate, setEditCandidate] = useState<any>(null);

    const [loadingPortfolios, setLoadingPortfilios] = useState(true);

    const loadPortfolios = useCleanup(() => {
        let cancelled = false;
        const run = async () => {
            const qs = new URLSearchParams({
                ...pagination.params,
            })
            const result = await api.get('portfolios/?' + qs)
            if(cancelled) {
                return;
            }
            setPortfolios(result.data.portfolios);
            setPortfolioSummary(result.data.portfolioSummary);
            setSummaryError(undefined);
            pagination.readMeta(result.data.meta);
            setLoadingPortfilios(false);
        }

        run().catch((err) => {
            if(cancelled) {
                return;
            }
            setLoadingPortfilios(false);
            setSummaryError(err);
        })

        return () => {
            cancelled = true;
        };
    });

    useEffect(() => {
        loadPortfolios()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const tableWithPagination = (
        <>
            <SimpleSortTable additionalClass="" data={portfolios} columns={columns} isLoading={loadingPortfolios} />
            <Pagination
                pagination={pagination.pagination}
                isLoading={loadingPortfolios}
                onPageChange={() => {}}
            />
        </>
    )

    const zones = usePortfolioListZones({
        onCreateClick: openCreateModal,
        tableComponent: tableWithPagination,
        isLoading: loadingPortfolios,
        portfolioSummary,
        summaryError,
    })

    if(loadingPortfolios) {
        return (
            <LoadingSpinner />
        )
    }

    return(
        <UniversalPageLayout
            documentTitle={routes.portfolio.name}
            testid='portfolio-list-main'
            heading={{
                crumbs: [{ label: routes.companiesPortfolio.menuName as string }]
            }}
            summary={zones.summary}
            actions={zones.actions}
        >
            {zones.table}
            <CreatePortfolioModal
                isOpen={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onCreated={() => {
                    setCreateModalOpen(false);
                    loadPortfolios();
                }}
            />
            { deletionCandidate && (
                <DeletePortfolioModal
                    onClose={() => setDeletionCandidate(null)}
                    isOpen
                    onDeleted={() => {
                        setDeletionCandidate(null);
                        loadPortfolios();
                    }}
                    portfolioId={deletionCandidate.id}
                    portfolioName={deletionCandidate.name}
                />
            )}
            { editCandidate && (
                <EditPortfolioModal
                    onClose={() => setEditCandidate(null)}
                    isOpen
                    onEdited={() => {
                        setEditCandidate(null);
                        loadPortfolios();
                    }}
                    portfolioId={editCandidate.id}
                    portfolioName={editCandidate.name}
                />
            )}
        </UniversalPageLayout>
    )
}

export default PortfolioList;
